import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { sectionService, PagedSectionsResult } from '../../services/sectionService';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, Eye, EyeOff, Layers, Power, FolderOpen } from 'lucide-react';

export const SectionList = () => {
  const [loading, setLoading] = useState(true);
  const [sections, setSections] = useState<PagedSectionsResult | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [includeDisabled, setIncludeDisabled] = useState(true);

  useEffect(() => {
    fetchSections();
  }, []);

  const fetchSections = async () => {
    try {
      setLoading(true);
      const data = await sectionService.getAllSections({
        includeCategories: true,
        page: 1,
        pageSize: 50,
      });
      setSections(data);
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'فشل تحميل الأقسام');
      console.error('Failed to fetch sections:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (id: string) => {
    try {
      await sectionService.toggleSectionStatus(id);
      toast.success('تم تغيير حالة القسم بنجاح');
      fetchSections();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'فشل تغيير حالة القسم');
    }
  };

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`هل أنت متأكد من حذف القسم "${name}"؟`)) {
      return;
    }

    try {
      await sectionService.deleteSection(id);
      toast.success('تم حذف القسم بنجاح');
      fetchSections();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'فشل حذف القسم');
    }
  };

  const filteredSections = sections?.sections.filter((section) => {
    const matchesSearch = section.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (section.description?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false);
    const matchesStatus = includeDisabled || section.isEnabled;
    return matchesSearch && matchesStatus;
  }) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-accent-dark mb-2">إدارة الأقسام</h1>
          <p className="text-accent-dark/70">إدارة وتنظيم أقسام اللعبة (المستوى الأعلى)</p>
        </div>
        <Link to="/admin/sections/create">
          <Button className="gap-2">
            <Plus className="w-4 h-4" />
            إضافة قسم جديد
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="البحث في الأقسام..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pr-10"
              />
            </div>
            <Button
              variant={includeDisabled ? 'secondary' : 'outline'}
              onClick={() => setIncludeDisabled(!includeDisabled)}
              className="gap-2"
            >
              {includeDisabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              {includeDisabled ? 'إظهار الكل' : 'المفعلة فقط'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Sections Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredSections.map((section) => (
          <Card key={section.id} className={`relative ${!section.isEnabled ? 'opacity-60' : ''}`}>
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-accent-greenLight/20 rounded-lg">
                    <Layers className="w-6 h-6 text-accent-greenDark" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{section.name}</CardTitle>
                    {section.description && (
                      <p className="text-sm text-gray-500 mt-1 line-clamp-2">{section.description}</p>
                    )}
                  </div>
                </div>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  section.isEnabled 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-red-100 text-red-800'
                }`}>
                  {section.isEnabled ? 'مفعل' : 'معطل'}
                </span>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {/* Stats */}
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <div className="flex items-center gap-1">
                    <FolderOpen className="w-4 h-4" />
                    <span>{section.categoryCount} فئة</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span>الترتيب: {section.displayOrder}</span>
                  </div>
                </div>

                {/* Categories Preview */}
                {section.categories && section.categories.length > 0 && (
                  <div className="text-xs text-gray-500">
                    <span className="font-medium">الفئات: </span>
                    {section.categories.slice(0, 3).map(c => c.name).join('، ')}
                    {section.categories.length > 3 && ` و${section.categories.length - 3} أخرى`}
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-2 border-t">
                  <Link to={`/admin/sections/edit/${section.id}`} className="flex-1">
                    <Button variant="outline" size="sm" className="w-full gap-1">
                      <Edit className="w-3 h-3" />
                      تعديل
                    </Button>
                  </Link>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleToggle(section.id)}
                    className="gap-1"
                  >
                    <Power className="w-3 h-3" />
                    {section.isEnabled ? 'تعطيل' : 'تفعيل'}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(section.id, section.name)}
                    className="text-red-600 hover:bg-red-50 gap-1"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Empty State */}
      {filteredSections.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <Layers className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">لا توجد أقسام</h3>
            <p className="text-gray-500 mb-4">ابدأ بإضافة أقسام جديدة لتنظيم فئات اللعبة</p>
            <Link to="/admin/sections/create">
              <Button className="gap-2">
                <Plus className="w-4 h-4" />
                إضافة قسم جديد
              </Button>
            </Link>
          </CardContent>
        </Card>
      )}

      {/* Total Count */}
      {sections && (
        <div className="text-center text-sm text-gray-500">
          إجمالي الأقسام: {sections.totalCount}
        </div>
      )}
    </div>
  );
};
